import React from 'react'
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Typography,
} from '@mui/material'
import _ from 'es-toolkit/compat'

import ConfirmDialog, { useConfirmDialog } from '@core/components/dialogs/dialog-confirm'
import Dropdown from '@core/components/dropdown/dropdown'

import { FormValues } from '../main-types'
import { categoryOptions } from './admin-add-customer'
import { useCustomer, useUpdateCustomer } from './admin-api'
import { AdminSettings } from './admin-settings-state'
import { FeatureFlagsSection } from './feature-flags-section'
import {
  defaultFormValues,
  handleFeatureFlagsChange,
  useFeatureFlagsProps,
} from './use-feature-flags-props'

export const AdminManageCustomer = ({ adminSettings }: { adminSettings: AdminSettings }) => {
  const { handleClose } = adminSettings
  const updateCustomer = useUpdateCustomer()
  const { data: bffCustomer } = useCustomer()

  const { customer } = bffCustomer!
  const confirmArchive = useConfirmDialog()

  const values = React.useMemo<FormValues>(() => {
    const result = _.merge({}, defaultFormValues, _.cloneDeep(customer))
    result.features.export_file = _.uniq(result.features.export_file ?? [])
    return result
  }, [customer])

  const handleChange = React.useCallback(
    (field: string, value: boolean | string | undefined) => {
      const updated = handleFeatureFlagsChange(values, field, value)
      updateCustomer.mutate({
        features: updated.features,
        hasOptionRationale: updated.hasOptionRationale,
      })
    },
    [values, updateCustomer],
  )

  const { getCheckboxProps, getFilenameConventionProps } = useFeatureFlagsProps({
    values,
    disabled: updateCustomer.isPending,
    onChange: handleChange,
  })

  return (
    <Box>
      <DialogContent sx={{ pt: '0px !important' }}>
        <Box typography="caption" fontWeight="bold">
          ID
        </Box>
        <Box>{customer.id}</Box>

        <Box typography="caption" fontWeight="bold" mt={1}>
          Customer Name
        </Box>
        <Box>{customer.name}</Box>

        <Box display="flex" justifyContent="space-between">
          <Box>
            <Box typography="caption" fontWeight="bold" mt={1}>
              Created At
            </Box>
            <Box>{new Date(customer.createdAt!).toLocaleString()}</Box>
          </Box>

          <Box>
            <Box typography="caption" fontWeight="bold" mt={1}>
              Updated At
            </Box>
            <Box>{new Date(customer.updatedAt!).toLocaleString()}</Box>
          </Box>
        </Box>
        <Box typography="caption" fontWeight="bold" mt={1}>
          SSO Connection Name
        </Box>
        <Box>{customer.ssoConnectionName || 'None'}</Box>

        <Box mt={1}>
          <Typography variant="subtitle2" gutterBottom>
            Category
          </Typography>
          <Dropdown
            value={
              categoryOptions.find((o) => o.value === (customer.category || 'regular')) ?? null
            }
            onChange={(option) => updateCustomer.mutate({ category: option.value })}
            options={categoryOptions}
            disabled={updateCustomer.isPending}
          />
        </Box>
        <FormControlLabel
          disabled={updateCustomer.isPending}
          slotProps={{ typography: { fontSize: 14 } }}
          sx={{ display: 'flex' }}
          control={<Checkbox size="small" color="error" />}
          checked={customer.isArchived ?? false}
          label="Archived"
          onChange={() => {
            if (customer.isArchived) {
              updateCustomer.mutate({ isArchived: false })
            } else {
              confirmArchive.open(() => updateCustomer.mutateAsync({ isArchived: true }))
            }
          }}
        />
        <Box typography="caption" color="error.main">
          Archived customers cannot be accessed by non-internal admin users.
        </Box>

        <FeatureFlagsSection
          getCheckboxProps={getCheckboxProps}
          getFilenameConventionProps={getFilenameConventionProps}
        />

        <ConfirmDialog
          state={confirmArchive}
          id="confirm-archive-customer"
          title="Archive Customer?"
          confirmText="Archive"
          content={
            <Typography>
              Archiving <strong>{customer.name}</strong> will prevent all non-internal admin users
              from accessing this customer. Are you sure?
            </Typography>
          }
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={updateCustomer.isPending} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Box>
  )
}
